// Lifted from schedugram-client/react-src/components/ui/data-table/data-table.tsx
import { useCallback, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { DataTableHeader } from "./data-table-header";
import { DataTableRow } from "./data-table-row";
import { DataTableSkeleton } from "./data-table-skeleton";
import type { DataTableProps, SortDirection, SortState } from "./types";

export function DataTable<T>({
  columns,
  data,
  getRowId,
  isLoading = false,
  loadingRowCount = 5,
  emptyState,
  onRowClick,
  rowClassName,
  defaultSortColumn,
  defaultSortDirection = null,
  onSortChange,
  sortComparator,
  stickyHeader = true,
  stickyOffset,
  className,
  tableClassName,
  showCellBorders = false,
}: DataTableProps<T>) {
  const [sortState, setSortState] = useState<SortState>({
    column: defaultSortColumn || null,
    direction: defaultSortColumn ? defaultSortDirection : null,
  });

  const handleSort = useCallback(
    (columnId: string) => {
      setSortState((prev) => {
        let newDirection: SortDirection;

        if (prev.column !== columnId) {
          newDirection = "asc";
        } else if (prev.direction === "asc") {
          newDirection = "desc";
        } else if (prev.direction === "desc") {
          newDirection = null;
        } else {
          newDirection = "asc";
        }

        const newState = {
          column: newDirection ? columnId : null,
          direction: newDirection,
        };

        onSortChange?.(columnId, newDirection);
        return newState;
      });
    },
    [onSortChange],
  );

  const sortedData = useMemo(() => {
    if (!sortState.column || !sortState.direction) return data;

    if (sortComparator) {
      return [...data].sort((a, b) => sortComparator(a, b, sortState.column!, sortState.direction));
    }

    const column = columns.find((c) => c.id === sortState.column);
    if (!column?.accessorKey) return data;

    const key = column.accessorKey;
    const direction = sortState.direction;

    return [...data].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return direction === "asc" ? 1 : -1;
      if (bVal == null) return direction === "asc" ? -1 : 1;

      if (typeof aVal === "string" && typeof bVal === "string") {
        const cmp = aVal.localeCompare(bVal);
        return direction === "asc" ? cmp : -cmp;
      }

      if (typeof aVal === "number" && typeof bVal === "number") {
        return direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      const cmp = String(aVal).localeCompare(String(bVal));
      return direction === "asc" ? cmp : -cmp;
    });
  }, [data, sortState, sortComparator, columns]);

  const renderBody = () => {
    if (isLoading) {
      return (
        <DataTableSkeleton
          columns={columns}
          rowCount={loadingRowCount}
          showCellBorders={showCellBorders}
        />
      );
    }

    if (sortedData.length === 0) {
      return (
        <tr>
          <td colSpan={columns.length} className="px-4 py-8 text-center text-sm text-neutral-500">
            {emptyState || "No data available"}
          </td>
        </tr>
      );
    }

    return sortedData.map((row, index) => (
      <DataTableRow
        key={getRowId(row)}
        row={row}
        rowIndex={index}
        columns={columns}
        isLast={index === sortedData.length - 1}
        onRowClick={onRowClick}
        rowClassName={rowClassName}
        showCellBorders={showCellBorders}
      />
    ));
  };

  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg border border-neutral-200 bg-white",
        "shadow-[0_1px_2px_rgba(16,24,40,0.06),0_1px_3px_rgba(16,24,40,0.1)]",
        className,
      )}
    >
      <div className="overflow-x-auto">
        <table className={cn("w-full", tableClassName)}>
          <DataTableHeader
            columns={columns}
            sortState={sortState}
            onSort={handleSort}
            stickyHeader={stickyHeader}
            stickyOffset={stickyOffset}
            showCellBorders={showCellBorders}
          />
          <tbody>{renderBody()}</tbody>
        </table>
      </div>
    </div>
  );
}
